import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, styled, Typography } from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  borderRadius,
  boxShadows,
  colors,
  fontSize,
  fontWeight,
  gaps,
  padding,
} from "theme/defaultStyle";
import DocumentEditIcon from "theme/icons/DocumentEditIcon";
import ActionButton from "controls/global/action-button/ActionButton";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import { getPolicyTransactionById } from "utils/api/policyTransactionApi";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import { dtoToRecord } from "../usePolicyTransactionList";
import PolicyDetailsPanel from "../quality-review/PolicyDetailsPanel";
import FileViewSection from "./FileViewSection";
import NotesSection from "./NotesSection";
import HistorySection from "./HistorySection";
import { TransferModal } from "../modals";
import { PERMISSIONS } from "utils/data/enum";

// Page-layout styled components mirror Policy Inventory's PolicyDetails page
// (src/pages/lookup/policy-details/PolicyDetails.tsx lines 27-76). Copied
// verbatim rather than exported because the originals aren't exported today;
// follow-up: promote to a shared details-layout module.
const ContainerBox = styled(Box)({
  position: "relative",
  margin: "24px 0 0 0",
  display: "flex",
  flexDirection: "column",
  gap: gaps.large1,
});

const SectionsContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: gaps.large3,
  maxWidth: "100%",
  background: "#fff",
  padding: padding.xlarge1,
  borderRadius: borderRadius.xlarge,
  boxShadow: boxShadows.boxShadow07,
});

const BreadcrumbContainer = styled("div")({
  display: "flex",
  justifyContent: "space-between",
});

const Breadcrumb = styled("div")({
  display: "flex",
  gap: gaps.xsmall2,
});

const BreadcrumbPathText = styled(Typography)({
  color: colors.blue17,
  fontSize: fontSize.large,
});

const BreadcrumbPolicyNumber = styled(Typography)({
  color: colors.blue15,
  fontSize: fontSize.large,
});

const PolicyHeadingContainer = styled("div")({
  display: "flex",
  gap: gaps.small1,
  color: colors.maroon02,
  alignItems: "center",
});

const PolicyHeading = styled(Typography)({
  fontSize: fontSize.xlarge1,
  fontWeight: fontWeight.bold2,
});

type LocationState = {
  id?: string;
  record?: PolicyManagementRecord;
  fromPolicyNumber?: string;
} | null;

const ProductDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const state = location.state as LocationState;
  const [{ profileSettings }] = useProfileSettingsCache();
  const hasPMEditAccess = profileSettings.userPermissionItemTypes.includes(PERMISSIONS.PIP_PM_EDIT);

  const [transferOpen, setTransferOpen] = useState(false);

  // Seed from nav state for an instant paint; on browser refresh / deep-link the
  // nav state is gone, so fall back to fetching the record by id from the URL.
  const [record, setRecord] = useState<PolicyManagementRecord | undefined>(
    state?.record
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (state?.record) {
      setRecord(state.record);
      return;
    }
    if (!id) return;
    let active = true;
    setIsLoading(true);
    getPolicyTransactionById(Number(id))
      .then((dto) => {
        if (!active) return;
        setRecord(dto ? dtoToRecord(dto) : undefined);
        setIsLoading(false);
      })
      .catch(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, state?.record]);

  // Re-fetches the current record from the server, bypassing nav state, so
  // actions like Transfer are reflected immediately without a page reload.
  const refreshRecord = () => {
    if (!id) return;
    getPolicyTransactionById(Number(id)).then((dto) => {
      setRecord(dto ? dtoToRecord(dto) : undefined);
    });
  };

  // Opening a different policy (e.g. via a File View policy-number link)
  // navigates to this same route, which re-renders in place and keeps the
  // current scroll position. Reset to the top so the newly opened policy's
  // header is visible instead of staying scrolled down at the prior section.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [record?.PolicyNumber]);

  if (isLoading && !record) {
    return (
      <ContainerBox>
        <SectionsContainer>
          <Typography sx={{ fontSize: fontSize.large, color: colors.grey11 }}>
            Loading policy details…
          </Typography>
        </SectionsContainer>
      </ContainerBox>
    );
  }

  if (!record) {
    return (
      <ContainerBox>
        <SectionsContainer>
          <Typography sx={{ fontSize: fontSize.large, color: colors.grey11 }}>
            Record not found.
          </Typography>
          <Box>
            <ActionButton
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/policyManagement")}
            >
              Back to Policy Management
            </ActionButton>
          </Box>
        </SectionsContainer>
      </ContainerBox>
    );
  }

  // Derive panel variant from workflow flags so the footer matches the tab
  // the record currently lives in (CQ banner, History closed-status row, etc.).
  const variant: "qr" | "pcq" | "cq" | "history" = record.isClosed
    ? "history"
    : record.isReadyToClose
      ? "cq"
      : record.isReadyToPreClose
        ? "pcq"
        : "qr";

  // Replicates the expanded-row drawer's own editable-by-variant rule
  // (QualityReviewTable/PreCloseTable pass `editable`; CloseQueueTable/
  // HistoryTable don't), so a field is enabled here exactly when it would be
  // enabled in the drawer for this same record.
  const editable = variant === "qr" || variant === "pcq";

  return (
    <ContainerBox>
      <BreadcrumbContainer>
        <Breadcrumb>
          <BreadcrumbPathText
            sx={{ cursor: "pointer" }}
            onClick={() => navigate("/policyManagement")}
          >
            Policy Management /{" "}
          </BreadcrumbPathText>
          <BreadcrumbPolicyNumber>
            Product Details ({record.PolicyNumber || "---"})
          </BreadcrumbPolicyNumber>
        </Breadcrumb>
        {state?.fromPolicyNumber && (
          <ActionButton
            variant="outlined"
            color="secondary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Back to {state.fromPolicyNumber}
          </ActionButton>
        )}
      </BreadcrumbContainer>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <PolicyHeadingContainer>
          <DocumentEditIcon width="24" height="24" color={colors.maroon02} />
          <PolicyHeading>
            Product Details ({record.PolicyNumber || "---"})
          </PolicyHeading>
        </PolicyHeadingContainer>
        {hasPMEditAccess && <ActionButton
          variant="outlined"
          color="secondary"
          startIcon={<SwapHorizIcon />}
          onClick={() => setTransferOpen(true)}
        >
          Transfer
        </ActionButton>}

      </Box>

      <PolicyDetailsPanel
        record={record}
        editable={editable}
        variant={variant}
        hideActions
        onRefresh={refreshRecord}
      />
      <FileViewSection record={record} />
      <NotesSection record={record} />
      <HistorySection record={record} />

      <TransferModal
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        record={record}
        onSubmit={() => refreshRecord()}
      />
    </ContainerBox>
  );
};

export default ProductDetails;
